import { formatPresetLabel, presetToDateRange } from '@server/time-presets';
import dayjs from 'dayjs';
import { CalendarIcon, Check, ChevronsUpDownIcon, Info, LockIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { Button } from '@/_shared/components/ui/button';
import { Calendar } from '@/_shared/components/ui/calendar';
import { ComboboxField } from '@/_shared/components/ui/combobox';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { useIsMobile } from '@/_shared/hooks/use-mobile';
import { cn } from '@/_shared/lib/utils';

import { filterValidParamOptions } from '../lib/dashboard-params';
import type { DashboardParam } from '../types';

function formatDate(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

function displayDate(iso: string): string {
  return dayjs(iso).format('MMM D, YYYY');
}

function daysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfMonth(): Date {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

interface TimeRangePreset {
  label: string;
  from: () => Date;
  to: () => Date;
}

const TIME_PRESETS: TimeRangePreset[] = [
  { label: 'Last 7 days', from: () => daysAgo(7), to: today },
  { label: 'Last 30 days', from: () => daysAgo(30), to: today },
  { label: 'Last 90 days', from: () => daysAgo(90), to: today },
  { label: 'This month', from: startOfMonth, to: today },
];

const DEFAULT_PRESET = TIME_PRESETS[1]!; // Last 30 days

interface Props {
  parameters: DashboardParam[];
  values: Record<string, string | string[]>;
  onChange: (params: Record<string, string | string[]>) => void;
  loadingParamNames?: Set<string>;
  /** Public share view: hides the "ask Lookout" affordance, since anonymous viewers can't chat. */
  isPublic?: boolean;
}

// Small info (ⓘ) icon + tooltip explaining the date-range behaviour and pointing users to chat.
// Only shown in the authenticated app (callers gate on !isPublic), since anonymous /pub viewers
// can't chat with Lookout.
function RangeInfoTooltip({
  ariaLabel,
  children,
}: {
  ariaLabel: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Tooltip>
      {/* Rendered as a <span>, not a nested <button>: these ⓘ tooltips live INSIDE the picker /
          locked-range buttons, and a <button> inside a <button> is invalid HTML. pointer-events-auto
          keeps it hoverable even when the parent button is aria-disabled (pointer-events:none). */}
      <TooltipTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
          className="pointer-events-auto inline-flex cursor-help text-muted-foreground transition-colors hover:text-foreground"
        >
          <Info className="h-3.5 w-3.5 shrink-0" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{children}</TooltipContent>
    </Tooltip>
  );
}

function FixedTimeRangeBadge({ param, isPublic }: { param: DashboardParam; isPublic?: boolean }): React.ReactElement {
  const preset = param.fixed_preset!;
  const label = formatPresetLabel(preset);

  // Resolve per render (cheap, pure) so the window follows new Date() across UTC midnight.
  // presetToDateRange throws on a malformed preset (a direct DB edit that skipped validateSpec).
  // /pub renders outside the app error boundary, so degrade to the label here — never throw.
  let dateRange: string | null = null;
  try {
    const { start, end } = presetToDateRange(preset, new Date());
    const startDisplay = displayDate(start.slice(0, 10));
    const endDisplay = displayDate(end.slice(0, 10));
    dateRange = startDisplay === endDisplay ? startDisplay : `${startDisplay} – ${endDisplay}`;
  } catch {
    // Malformed persisted preset — fall through with the label only (dateRange stays null).
  }

  return (
    <Button
      variant="secondary"
      aria-disabled
      className="justify-start gap-2 text-left font-normal"
      Icon={<LockIcon className="h-3.5 w-3.5" />}
    >
      <span className="truncate">
        {label}
        {dateRange ? ` · ${dateRange}` : ''}
      </span>
      {!isPublic && (
        <RangeInfoTooltip ariaLabel="Why is this date range locked?">
          {`This dashboard always shows a fixed window — ${label}. In chat, ask Lookout to set a different window (e.g. "use the last 7 days", "lock to this month"), or to switch to a date picker viewers can adjust themselves.`}
        </RangeInfoTooltip>
      )}
    </Button>
  );
}

function TimeRangeParam({
  startValue,
  endValue,
  onRangeChange,
  isMobile,
  isPublic,
}: {
  startValue: string;
  endValue: string;
  onRangeChange: (start: string, end: string) => void;
  isMobile: boolean;
  isPublic?: boolean;
}): React.ReactElement {
  const fromDate = startValue ? new Date(startValue + 'T00:00:00') : DEFAULT_PRESET.from();
  const toDate = endValue ? new Date(endValue + 'T00:00:00') : DEFAULT_PRESET.to();

  const [draft, setDraft] = useState<DateRange | null>(null);

  const committed: DateRange = { from: fromDate, to: toDate };
  const selected: DateRange = draft ?? committed;

  function handleSelect(range: DateRange | undefined): void {
    if (!range) {
      setDraft(null);
      return;
    }
    if (range.from && range.to) {
      setDraft(null);
      onRangeChange(formatDate(range.from), formatDate(range.to));
    } else {
      setDraft(range);
    }
  }

  function handlePreset(preset: TimeRangePreset): void {
    setDraft(null);
    onRangeChange(formatDate(preset.from()), formatDate(preset.to()));
  }

  const displayFrom = startValue ? displayDate(startValue) : displayDate(formatDate(DEFAULT_PRESET.from()));
  const displayTo = endValue ? displayDate(endValue) : displayDate(formatDate(DEFAULT_PRESET.to()));

  const startMonth = new Date(1900, 0);
  const endMonth = new Date(new Date().getFullYear() + 5, 11);

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) setDraft(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          className={cn(
            'justify-start gap-2 text-left font-normal',
            !startValue && !endValue && 'text-muted-foreground',
          )}
          Icon={<CalendarIcon className="h-3.5 w-3.5" />}
        >
          <span className="truncate">
            {displayFrom} - {displayTo}
          </span>
          {!isPublic && (
            <RangeInfoTooltip ariaLabel="About this date range">
              {`Pick any dates, or use a preset. In chat you can ask Lookout to set the default range this opens with (e.g. "default to the last 7 days"), or to lock the dashboard to a fixed window — viewers then see a read-only range instead of this picker.`}
            </RangeInfoTooltip>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="w-24 space-y-1 border-r border-border p-2 md:w-28">
            {TIME_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="tertiary"
                className="w-full justify-start px-0 text-xs md:px-3"
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <Calendar
            mode="range"
            selected={selected}
            onSelect={handleSelect}
            numberOfMonths={isMobile ? 1 : 2}
            defaultMonth={draft?.from ?? fromDate}
            captionLayout="dropdown"
            startMonth={startMonth}
            endMonth={endMonth}
            resetOnSelect
            showOutsideDays={false}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SelectParam({
  param,
  value,
  onValueChange,
  loading,
}: {
  param: DashboardParam;
  value: string;
  onValueChange: (value: string) => void;
  loading?: boolean;
}): React.ReactElement {
  const options = useMemo(() => filterValidParamOptions(param.options), [param.options]);

  return (
    <ComboboxField
      mode="single"
      variant="popup"
      options={options}
      value={value}
      onValueChange={onValueChange}
      placeholder={param.label}
      aria-label={param.label}
      loading={loading}
      className="w-auto max-w-64 min-w-35 text-xs"
    />
  );
}

// Multi-select uses a plain Popover + checkbox list (not Combobox — see AI ticket to migrate it). The
// checkbox / trigger styling mirrors the Combobox family so it reads as one bar. An empty selection is a
// valid state; there is no min-1 lock.
function MultiSelectParam({
  param,
  selected,
  onSelectedChange,
  loading,
}: {
  param: DashboardParam;
  selected: string[];
  onSelectedChange: (values: string[]) => void;
  loading?: boolean;
}): React.ReactElement {
  const options = useMemo(() => filterValidParamOptions(param.options), [param.options]);
  const noOptions = !loading && options.length === 0;

  function toggleOption(value: string): void {
    onSelectedChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }

  // Label the raw selection so it matches what the panel query filters on.
  const label =
    selected.length === 0
      ? param.label
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0] ?? param.label)
        : `${param.label} (${selected.length})`;

  const trigger = (
    <Button
      variant="secondary"
      loading={loading}
      disabled={noOptions}
      suffixIcon={<ChevronsUpDownIcon className="size-4 shrink-0 text-grey-500" />}
      className="w-auto max-w-64 min-w-35 justify-between gap-2 text-left text-xs font-normal"
    >
      <span className="truncate">{label}</span>
    </Button>
  );

  // Misconfigured / empty options: keep the control visible but disabled, with the reason on hover.
  if (noOptions) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex">
            {trigger}
          </span>
        </TooltipTrigger>
        <TooltipContent>No valid options — check the dashboard spec</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-56 rounded-xl border-grey-100 p-1.5">
        <div className="max-h-60 space-y-0.5 overflow-y-auto">
          {options.map((opt) => {
            const isSelected = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="checkbox"
                aria-checked={isSelected}
                title={opt.label}
                onClick={() => toggleOption(opt.value)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-grey-800 transition-colors hover:bg-grey-50"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-sm border border-border',
                    isSelected && 'border-grey-900 bg-grey-900',
                  )}
                >
                  {isSelected && <Check className="size-3 text-grey-0" strokeWidth={2} />}
                </span>
                <span className="min-w-0 flex-1 truncate text-left">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DashboardParamBar({
  parameters,
  values,
  onChange,
  loadingParamNames,
  isPublic,
}: Props): React.ReactElement {
  const isMobile = useIsMobile();
  // Ref to hold the latest values for stable callbacks
  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  });

  const handleChange = useCallback(
    (key: string, value: string | string[]) => {
      onChange({ ...valuesRef.current, [key]: value });
    },
    [onChange],
  );

  const handleTimeRangeChange = useCallback(
    (name: string, start: string, end: string) => {
      onChange({
        ...valuesRef.current,
        [`${name}_start`]: start,
        [`${name}_end`]: end,
      });
    },
    [onChange],
  );

  if (parameters.length === 0) {
    return <></>;
  }

  return (
    <div className="scrollbar-none flex items-center gap-2 overflow-x-auto border-b border-border px-6 py-2.5 *:shrink-0">
      {parameters.map((param) => {
        switch (param.type) {
          case 'time_range': {
            if (param.fixed_preset) {
              return <FixedTimeRangeBadge key={param.name} param={param} isPublic={isPublic} />;
            }
            const startKey = `${param.name}_start`;
            const endKey = `${param.name}_end`;
            const startVal = typeof values[startKey] === 'string' ? values[startKey] : '';
            const endVal = typeof values[endKey] === 'string' ? values[endKey] : '';
            return (
              <TimeRangeParam
                key={param.name}
                startValue={startVal}
                endValue={endVal}
                onRangeChange={(s, e) => handleTimeRangeChange(param.name, s, e)}
                isMobile={isMobile}
                isPublic={isPublic}
              />
            );
          }
          case 'select': {
            const raw = values[param.name];
            const val = typeof raw === 'string' ? raw : '';
            return (
              <SelectParam
                key={param.name}
                param={param}
                value={val}
                onValueChange={(v) => handleChange(param.name, v)}
                loading={loadingParamNames?.has(param.name)}
              />
            );
          }
          case 'multi_select': {
            const raw = values[param.name];
            const selected: string[] = Array.isArray(raw) ? raw : [];
            return (
              <MultiSelectParam
                key={param.name}
                param={param}
                selected={selected}
                onSelectedChange={(v) => handleChange(param.name, v)}
                loading={loadingParamNames?.has(param.name)}
              />
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}
